/**
 * Shared Chart Styles
 *
 * Reusable styled components for all chart types.
 * Uses superstyled from ZUI for consistent theming.
 */

import Box from '@zenbusiness/zui/Box';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import Text from '@zenbusiness/zui/Text';

// ========================================
// CHART WRAPPER
// ========================================

export const ChartWrapper = superstyled({
  name: 'Chart',
  slot: 'Wrapper',
  tag: Box,
  styles: ({ theme }) => ({
    width: '100%',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.rounded.lg,
    border: `1px solid ${theme.palette.border.main}`,
    boxShadow: theme.shadows[1]
  })
});

export const ChartTitle = superstyled({
  name: 'Chart',
  slot: 'Title',
  tag: Text,
  styles: ({ theme }) => ({
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  })
});

// ========================================
// EMPTY STATE
// ========================================

export const EmptyState = superstyled({
  name: 'Chart',
  slot: 'EmptyState',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.rounded.md,
    color: theme.palette.text.tertiary || theme.palette.grey[400],
    fontStyle: 'italic'
  })
});

// ========================================
// TOOLTIP
// ========================================

export const TooltipContainer = superstyled({
  name: 'Chart',
  slot: 'TooltipContainer',
  tag: Box,
  styles: ({ theme }) => ({
    backgroundColor: theme.palette.fixed.grey[950] || theme.palette.grey[950],
    color: theme.palette.text.white || theme.palette.common.white,
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: `1px solid ${theme.palette.fixed.grey[900] || theme.palette.grey[900]}`,
    boxShadow: '0 0.25rem 0.75rem rgba(0, 0, 0, 0.15)',
    maxWidth: '18.75rem'
  })
});

export const TooltipTitle = superstyled({
  name: 'Chart',
  slot: 'TooltipTitle',
  tag: Text,
  styles: ({ theme }) => ({
    marginBottom: '0.25rem',
    color: theme.palette.text.white || theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.fixed.grey[900] || theme.palette.grey[900]}`,
    paddingBottom: '0.5rem'
  })
});

export const TooltipRow = superstyled({
  name: 'Chart',
  slot: 'TooltipRow',
  tag: Box,
  styles: () => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.35rem',
    marginTop: '0.375rem',
    '& > span:first-of-type': {
      opacity: 0.7
    },
    '& > strong, & > span:last-of-type': {
      fontWeight: 500
    }
  })
});

export const TooltipBadge = superstyled({
  name: 'Chart',
  slot: 'TooltipBadge',
  tag: Text,
  styles: ({ color }) => ({
    display: 'inline-block',
    marginTop: '0.625rem',
    padding: '0.1875rem 0.625rem',
    backgroundColor: color || 'rgba(255, 255, 255, 0.15)',
    borderRadius: '0.25rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  })
});

// ========================================
// LEGEND
// ========================================

export const LegendContainer = superstyled({
  name: 'Chart',
  slot: 'LegendContainer',
  tag: Box,
  styles: () => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '1rem',
    padding: '0.75rem 0'
  })
});

export const LegendItem = superstyled({
  name: 'Chart',
  slot: 'LegendItem',
  tag: Box,
  styles: () => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    cursor: 'default'
  })
});

export const LegendColor = superstyled({
  name: 'Chart',
  slot: 'LegendColor',
  tag: 'span',
  styles: ({ variant }) => ({
    width: '0.75rem',
    height: variant === 'line' ? '0.1875rem' : '0.75rem',
    borderRadius: variant === 'line' ? 0 : '0.125rem',
    flexShrink: 0
  })
});

export const LegendText = superstyled({
  name: 'Chart',
  slot: 'LegendText',
  tag: Text,
  styles: ({ theme }) => ({
    color: theme.palette.text.secondary
  })
});

// ========================================
// QUADRANT LABELS (for Scatter Charts)
// ========================================

export const QuadrantLabel = superstyled({
  name: 'Chart',
  slot: 'QuadrantLabel',
  tag: Text,
  styles: () => ({
    opacity: 0.4,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    pointerEvents: 'none',
    userSelect: 'none'
  })
});
